import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronRight, Sparkles, Loader2, Type } from 'lucide-react';
import AppleHeader from '../components/AppleHeader';
import { supabase } from '../lib/supabase';
import { GENERATE_AD_URL, GENERATE_AUTH_TOKEN } from '../config';
import { postGenerateAd, GenerateAdError } from '../lib/generateAdRequest';
import GeneratedImagesInline from '../components/GeneratedImagesInline';
import CachedImg from '../components/CachedImg';
import LoadingAnimation from '../components/LoadingAnimation';
import SubscriptionLimitModal from '../components/SubscriptionLimitModal';
import { retrySupabase } from '../utils/retry';
import { trackInteraction } from '../utils/trackInteraction';
import {
  loadLastCrearAnuncioState,
  mergeLastCrearAnuncioState,
  type CrearAnuncioBackgroundState,
  type CrearAnuncioObjetivo,
} from '../utils/crearAnuncioStorage';
import { preloadGeneratedImages, type PreloadedImage } from '../utils/preloadGeneratedImages';

interface Fondo {
  id: number;
  link_img: string;
  categoria: string;
  nombre?: string;
  rubros?: string[];
}

export const ElegirFondoAnuncio: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsParam = params.get('ids') || '';
  const ids = useMemo(() => idsParam.split(',').map((s) => s.trim()).filter(Boolean).filter((s) => s !== '0'), [idsParam]);
  const modo = (params.get('modo') as 'rapido' | 'personalizado') || 'personalizado';
  const composicion = (params.get('composicion') as 'modelo' | 'producto') || 'modelo';
  const tipo = (params.get('tipo') as 'imagen' | 'video') || 'imagen';
  const formato = (params.get('formato') as 'post' | 'story' | 'square') || 'post';
  const cantidadImagenesRaw = params.get('cantidad_imagenes');
  const cantidadImagenes = useMemo(() => {
    const n = Number(cantidadImagenesRaw);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [cantidadImagenesRaw]);
  const objetivo = params.get('objetivo') || '';
  const detalles_oferta = params.get('detalles_oferta') || '';
  const modoPro = params.get('modo_pro') === '1';

  const savedState = useMemo(() => loadLastCrearAnuncioState(), []);
  const savedBackground = savedState?.background;
  const FASHION_LABEL = 'Moda e Indumentaria';
  const isFashion = ((savedState?.rubro as string | undefined) ?? FASHION_LABEL) === FASHION_LABEL;

  // Modelo params forwarded
  const modeloMode = (params.get('modelo_mode') as 'ia' | 'guardados' | 'personalizado') || 'ia';
  const modeloGuardadoId = params.get('modelo_guardado_id') || '';
  const modeloGuardadoNombre = params.get('modelo_guardado_nombre') || '';
  const pose = params.get('pose') || '';
  const genero = params.get('genero') || '';
  const edad = params.get('edad') || '';
  const tipoCuerpo = params.get('tipo_cuerpo') || '';
  const tonoPiel = params.get('tono_piel') || '';
  const colorPelo = params.get('color_pelo') || '';
  const largoPelo = params.get('largo_pelo') || '';
  const descModelo = params.get('desc_modelo') || '';

  const [fondos, setFondos] = useState<Fondo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const backgroundModeParam = params.get('background_mode') as 'ia' | 'blanco' | 'elegido' | 'descripcion' | null;
  const backgroundIdParam = params.get('background_id');
  const backgroundUrlParam = params.get('background_url') || '';

  const [selectedBgMode, setSelectedBgMode] = useState<'ia' | 'blanco' | 'elegido' | 'descripcion' | ''>(() => {
    if (backgroundModeParam && ['ia', 'blanco', 'elegido', 'descripcion'].includes(backgroundModeParam)) {
      return backgroundModeParam as 'ia' | 'blanco' | 'elegido' | 'descripcion';
    }
    if (savedBackground?.mode && ['ia', 'blanco', 'elegido', 'descripcion'].includes(savedBackground.mode)) {
      return savedBackground.mode as 'ia' | 'blanco' | 'elegido' | 'descripcion';
    }
    return 'blanco';
  });
  const [selectedBgUrl, setSelectedBgUrl] = useState<string>(() => {
    if (backgroundModeParam === 'elegido' && backgroundUrlParam) return backgroundUrlParam;
    if (savedBackground?.url) return savedBackground.url;
    return '';
  });
  const [selectedBgId, setSelectedBgId] = useState<number | null>(() => {
    if (backgroundModeParam === 'elegido' && backgroundIdParam) {
      const parsed = Number(backgroundIdParam);
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (typeof savedBackground?.id === 'number') {
      return savedBackground.id;
    }
    if (savedBackground?.id === null) return null;
    return null;
  });

  const [backgroundText, setBackgroundText] = useState<string>(() => {
    const fromParam = params.get('background_text');
    if (typeof fromParam === 'string' && fromParam) return fromParam;
    if (savedBackground?.text) return savedBackground.text;
    return '';
  });

  // In personalized mode: non-fashion always goes to text step; fashion goes to text when composicion === 'producto'
  const shouldGoToText = useMemo(() => {
    if (modo !== 'personalizado') return false;
    if (!isFashion) return true;
    return composicion === 'producto';
  }, [modo, isFashion, composicion]);

  // Generation state (used when composicion === 'producto')
  const [generating, setGenerating] = useState(false);
  const [completeProgressNow, setCompleteProgressNow] = useState<boolean>(false);
  const [genError, setGenError] = useState<React.ReactNode | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [generatedImageIds, setGeneratedImageIds] = useState<number[]>([]);
  const [companyIdForImages, setCompanyIdForImages] = useState<string>('');
  const willShowResultsRef = useRef<boolean>(false);
  const consecutiveGenErrorsRef = useRef<number>(0);
  // Ref to block rapid clicks before React re-renders
  const generatingLockRef = useRef<boolean>(false);
  // Progress duration differs by mode: 70s for quick, 35s for personalized
  const progressDurationMs = useMemo(() => (modo === 'rapido' ? 80000 : 35000), [modo]);
  // Prevent click-through when closing results
  const [clickShield, setClickShield] = useState<boolean>(false);

  // Subscription limit modal state (same UX/messages as Estudio Virtual)
  const [showSubscriptionLimitModal, setShowSubscriptionLimitModal] = useState<boolean>(false);
  const [subscriptionLimitType, setSubscriptionLimitType] = useState<'free-trial-ended' | 'plan-limit-reached'>('free-trial-ended');

  // Scroll to top on mount (iOS Safari fix)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // Helpers copied from ImageConfiguration for billing cycle anchored to paid_plan_start_date
  const getBillingCycleRange = (paidPlanStartDate: string) => {
    const now = new Date();
    const subscriptionDate = new Date(paidPlanStartDate);
    const anchorDay = subscriptionDate.getDate();

    const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
    const anchorFor = (year: number, month: number) => {
      const dim = daysInMonth(year, month);
      const day = Math.min(anchorDay, dim);
      return new Date(year, month, day, 0, 0, 0, 0);
    };

    let start = anchorFor(now.getFullYear(), now.getMonth());
    if (start.getTime() > now.getTime()) {
      const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      start = anchorFor(prevMonth.getFullYear(), prevMonth.getMonth());
    }

    if (start.getTime() < subscriptionDate.getTime()) {
      start = new Date(
        subscriptionDate.getFullYear(),
        subscriptionDate.getMonth(),
        subscriptionDate.getDate(),
        0, 0, 0, 0
      );
    }

    const nextMonth = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    const end = anchorFor(nextMonth.getFullYear(), nextMonth.getMonth());
    return { start: start.toISOString(), end: end.toISOString() };
  };

  const checkSubscriptionLimits = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await retrySupabase(async () =>
        await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single()
      );
      if (!profile?.id_company) throw new Error('No company associated with user');

      const { data: companyProfile } = await retrySupabase(async () =>
        await supabase
          .from('company_profiles')
          .select('plan, imagenes_restantes, paid_plan_start_date')
          .eq('id_company', profile.id_company)
          .single()
      );
      if (!companyProfile) throw new Error('No company profile found');

      const remaining = (companyProfile as any).imagenes_restantes as number | null | undefined;
      if (typeof remaining === 'number' && remaining <= 0) {
        const isFree = (companyProfile as any).plan === 'gratis';
        setSubscriptionLimitType(isFree ? 'free-trial-ended' : 'plan-limit-reached');
        setShowSubscriptionLimitModal(true);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Error checking subscription limits:', err);
      return true; // Do not block on error
    }
  };

  useEffect(() => {
    const fetchFondos = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data, error } = await retrySupabase(async () =>
          await supabase
            .from('fondos')
            .select('id, link_img, categoria, nombre, rubros')
        );
        if (error) throw error;
        const rubroActual = (savedState?.rubro as string | undefined) || FASHION_LABEL;
        const filtered = ((data as any) || []).filter((f: any) => {
          const arr = (f?.rubros as string[] | undefined) || [];
          if (arr.length === 1 && arr[0] === 'all') return true;
          return arr.includes(rubroActual);
        });
        setFondos(filtered);
      } catch (e) {
        console.error('Error cargando fondos', e);
        setError(e instanceof Error ? e.message : 'Error al cargar fondos');
      } finally {
        setLoading(false);
      }
    };
    fetchFondos();
  }, []);

  const fondosPorCategoria = useMemo(() => {
    const map = new Map<string, Fondo[]>();
    for (const f of fondos) {
      const key = f.categoria || 'Otros';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(f);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [fondos]);

  // Horizontal category selector (like Estudio Virtual)
  const [activeCategoria, setActiveCategoria] = useState<string>('');
  const categorias = useMemo(() => fondosPorCategoria.map(([c]) => c), [fondosPorCategoria]);
  useEffect(() => {
    if (!activeCategoria && categorias.length > 0) {
      setActiveCategoria(categorias[0]);
    }
  }, [categorias, activeCategoria]);
  const itemsCategoriaActual = useMemo(() => {
    const entry = fondosPorCategoria.find(([c]) => c === activeCategoria);
    return entry ? entry[1] : [];
  }, [fondosPorCategoria, activeCategoria]);

  const buildForwardParams = () => {
    const qp = new URLSearchParams();
    qp.set('ids', ids.join(','));
    qp.set('modo', modo);
    if (objetivo) qp.set('objetivo', objetivo);
    if (detalles_oferta.trim()) qp.set('detalles_oferta', detalles_oferta.trim());
    qp.set('composicion', composicion);
    qp.set('tipo', tipo);
    qp.set('formato', formato);
    if (cantidadImagenes) qp.set('cantidad_imagenes', String(cantidadImagenes));
    if (pose) qp.set('pose', pose);
    qp.set('modelo_mode', modeloMode);
    if (modeloMode === 'guardados' && modeloGuardadoId) {
      qp.set('modelo_guardado_id', modeloGuardadoId);
      if (modeloGuardadoNombre) qp.set('modelo_guardado_nombre', modeloGuardadoNombre);
    } else if (modeloMode === 'personalizado') {
      if (genero) qp.set('genero', genero);
      if (edad) qp.set('edad', edad);
      if (tipoCuerpo) qp.set('tipo_cuerpo', tipoCuerpo);
      if (tonoPiel) qp.set('tono_piel', tonoPiel);
      if (colorPelo) qp.set('color_pelo', colorPelo);
      if (largoPelo) qp.set('largo_pelo', largoPelo);
      if (descModelo.trim()) qp.set('desc_modelo', descModelo.trim());
    }
    return qp;
  };

  // Pickers: set selection but do not navigate yet
  const pickFondoIA = () => {
    setSelectedBgMode('ia');
    setSelectedBgUrl('');
    setSelectedBgId(null);
  };

  const pickFondoBlanco = () => {
    setSelectedBgMode('blanco');
    setSelectedBgUrl('');
    setSelectedBgId(null);
  };

  const pickFondoImagen = (url: string, id: number) => {
    setSelectedBgMode('elegido');
    setSelectedBgUrl(url);
    setSelectedBgId(id);
  };

  useEffect(() => {
    const trimmedDetalles = detalles_oferta.trim();
    const allowedObjetivos: CrearAnuncioObjetivo[] = ['mostrar', 'oferta', 'lanzamiento', 'evento'];
    const objetivoValue = allowedObjetivos.includes(objetivo as CrearAnuncioObjetivo)
      ? (objetivo as CrearAnuncioObjetivo)
      : undefined;

    let backgroundPayload: CrearAnuncioBackgroundState | null = null;
    if (selectedBgMode) {
      backgroundPayload = { mode: selectedBgMode };
      if (selectedBgMode === 'elegido') {
        if (typeof selectedBgId === 'number' && Number.isFinite(selectedBgId)) {
          backgroundPayload.id = selectedBgId;
        }
        if (selectedBgUrl) {
          backgroundPayload.url = selectedBgUrl;
        }
      }
      if (selectedBgMode === 'descripcion' && backgroundText.trim()) {
        backgroundPayload.text = backgroundText.trim();
      }
    }

    mergeLastCrearAnuncioState({
      modo,
      selectedVariantIds: ids,
      composicion,
      tipo,
      formato,
      cantidadImagenes: cantidadImagenes ?? undefined,
      objetivo: objetivoValue ?? null,
      detallesOferta: trimmedDetalles ? trimmedDetalles : null,
      background: backgroundPayload,
    });
  }, [
    ids,
    modo,
    composicion,
    tipo,
    formato,
    cantidadImagenes,
    objetivo,
    detalles_oferta,
    selectedBgMode,
    selectedBgId,
    selectedBgUrl,
    backgroundText,
  ]);

  const handleContinue = () => {
    if (!selectedBgMode) return;
    const qp = buildForwardParams();
    qp.set('background_mode', selectedBgMode);
    if (selectedBgMode === 'elegido' && selectedBgId !== null) {
      qp.set('background_id', String(selectedBgId));
    }
    if (selectedBgMode === 'elegido' && selectedBgUrl) {
      qp.set('background_url', selectedBgUrl);
    }
    if (selectedBgMode === 'descripcion' && backgroundText.trim()) {
      qp.set('background_text', backgroundText.trim());
    }
    try { if (selectedBgMode === 'descripcion') trackInteraction('escena_prompt'); } catch {}
    const nextUrl = `/redes-sociales/crear-anuncio/seleccionar-composicion?${qp.toString()}`;
    navigate(nextUrl);
  };

  const handleGenerate = async () => {
    // Immediate lock to prevent rapid clicks
    if (generatingLockRef.current) return;
    generatingLockRef.current = true;

    if (composicion !== 'producto') { generatingLockRef.current = false; return; } // Safety guard
    if (!selectedBgMode || ids.length === 0) { generatingLockRef.current = false; return; }
    try {
      // Show overlay immediately, then check limits
      setGenerating(true);
      setCompleteProgressNow(false);
      setGenError(null);
      const canGenerate = await checkSubscriptionLimits();
      if (!canGenerate) { setGenerating(false); generatingLockRef.current = false; return; }
      try { if (selectedBgMode === 'descripcion') trackInteraction('escena_prompt'); } catch {}

      // 1) Get user and company_id
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      const { data: profile } = await retrySupabase(async () =>
        await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single()
      );

      if (!profile?.id_company) throw new Error('No se encontró la empresa del usuario');

      // 2) Build format description
      let formatName = '';
      switch (formato) {
        case 'story':
          formatName = '9:16';
          break;
        case 'square':
          formatName = '1:1';
          break;
        case 'post':
        default:
          formatName = '4:5';
      }

      // 3) Determine ids to use
      const variantIds = ids;
      const mode = variantIds.length > 1 ? 'multiple' : 'single';

      // 4) Build request payload (include background selection, no composition step here)
      const requestData: Record<string, any> = {
        user_id: user.id,
        variante_id: variantIds,
        mode,
        modo,
        estilo: 'automatico',
        tipo,
        cantidad_imagenes: tipo === 'imagen' ? (cantidadImagenes ?? undefined) : undefined,
        Format: formatName,
        objetivo: objetivo || undefined,
        detalles_oferta: detalles_oferta.trim() ? detalles_oferta.trim() : undefined,
        composicion: composicion,
        background_mode: selectedBgMode,
        background_id:
          (selectedBgMode === 'elegido' && typeof selectedBgId === 'number' && Number.isFinite(selectedBgId))
            ? selectedBgId
            : (selectedBgMode === 'blanco' && modo === 'personalizado' ? 5 : undefined),
        background_url: selectedBgMode === 'elegido' && !(typeof selectedBgId === 'number' && Number.isFinite(selectedBgId)) ? (selectedBgUrl || undefined) : undefined,
        background_text: selectedBgMode === 'descripcion' && backgroundText.trim() ? backgroundText.trim() : undefined,
        modo_pro: modoPro,
      };

      // 5) Call webhook
      try { console.log('[ElegirFondoAnuncio] handleGenerate payload', requestData); } catch {}
      const data: any = await postGenerateAd(requestData);
      try { console.log('[ElegirFondoAnuncio] handleGenerate json', data); } catch {}
      if (Array.isArray(data?.imagenes_generadas_ids) && data.imagenes_generadas_ids.length > 0) {
        willShowResultsRef.current = true;
        setGeneratedImageIds(data.imagenes_generadas_ids as number[]);
        setCompanyIdForImages((data.id_company as string) || profile.id_company);
        setShowResults(true);
        setCompleteProgressNow(true);
        try { window.sessionStorage.setItem('atlas:crear-anuncio:has-generated', '1'); } catch {}
      } else {
        throw new Error('No se recibieron imágenes generadas.');
      }
    } catch (e) {
      console.error(e);
      setCompleteProgressNow(true);
      if (e instanceof GenerateAdError && e.isActionable) {
        setGenError(e.message);
      } else {
        const next = (consecutiveGenErrorsRef.current || 0) + 1;
        consecutiveGenErrorsRef.current = next;
        if (next >= 2) {
          setGenError(
            <span>
              Se produjo un error. No se consumieron imágenes. Es posible que los servidores estén momentaneamente en mantenimento. Si prefieres puedes contactarte con el{' '}
              <button
                type="button"
                onClick={() => navigate('/ayuda')}
                className="font-semibold underline"
              >
                servicio de ayuda
              </button>
              .
            </span>
          );
        } else {
          setGenError('Se produjo un error. No se consumieron imágenes. Por favor, volvé a intentarlo.');
        }
      }
      // generating will be set to false by LoadingAnimation onCompleted
      generatingLockRef.current = false;
    }
  };

  // State for preloaded images (for generate more)
  const [preloadedImagesForMore, setPreloadedImagesForMore] = useState<PreloadedImage[] | null>(null);

  const handleGenerateMore = async (): Promise<{ imageIds?: number[]; companyId?: string } | void> => {
    // Immediate lock to prevent rapid clicks
    if (generatingLockRef.current) return;
    generatingLockRef.current = true;

    try {
      setGenError(null);
      setCompleteProgressNow(false);
      setGenerating(true);
      const canGenerate = await checkSubscriptionLimits();
      if (!canGenerate) { setGenerating(false); generatingLockRef.current = false; return; }
      try { if (selectedBgMode === 'descripcion') trackInteraction('escena_prompt'); } catch {}

      // 1) Get user and company_id
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No se encontró la empresa del usuario');

      // 2) Build format description
      let formatName = '';
      switch (formato) {
        case 'story':
          formatName = '9:16';
          break;
        case 'square':
          formatName = '1:1';
          break;
        case 'post':
        default:
          formatName = '4:5';
      }

      // 3) Determine ids to use (use current ids)
      const variantIds = ids;
      const mode = variantIds.length > 1 ? 'multiple' : 'single';

      // 4) Build request payload (same settings, fewer images)
      const requestData: Record<string, any> = {
        user_id: user.id,
        variante_id: variantIds,
        mode,
        modo,
        estilo: 'automatico',
        tipo,
        cantidad_imagenes: tipo === 'imagen' ? 2 : undefined,
        Format: formatName,
        objetivo: objetivo || undefined,
        detalles_oferta: detalles_oferta.trim() ? detalles_oferta.trim() : undefined,
        composicion: composicion,
        background_mode: selectedBgMode,
        background_id:
          (selectedBgMode === 'elegido' && typeof selectedBgId === 'number' && Number.isFinite(selectedBgId))
            ? selectedBgId
            : (selectedBgMode === 'blanco' && modo === 'personalizado' ? 5 : undefined),
        background_url: selectedBgMode === 'elegido' && !(typeof selectedBgId === 'number' && Number.isFinite(selectedBgId)) ? (selectedBgUrl || undefined) : undefined,
        background_text: selectedBgMode === 'descripcion' && backgroundText.trim() ? backgroundText.trim() : undefined,
        modo_pro: modoPro,
      };

      // 5) Call webhook
      try { console.log('[ElegirFondoAnuncio] handleGenerateMore payload', requestData); } catch {}
      const data: any = await postGenerateAd(requestData);
      try { console.log('[ElegirFondoAnuncio] handleGenerateMore json', data); } catch {}
      if (Array.isArray(data?.imagenes_generadas_ids) && data.imagenes_generadas_ids.length > 0) {
        willShowResultsRef.current = true;
        const newImageIds = data.imagenes_generadas_ids as number[];
        const companyIdResult = (data.id_company as string) || profile.id_company;
        
        setShowResults(true);
        setCompleteProgressNow(true);
        generatingLockRef.current = false;
        try { window.sessionStorage.setItem('atlas:crear-anuncio:has-generated', '1'); } catch {}
        // Do NOT update generatedImageIds — that changes the prop and triggers a full re-fetch
        return { imageIds: newImageIds, companyId: companyIdResult };
      } else {
        throw new Error('No se recibieron imágenes generadas.');
      }
    } catch (e) {
      console.error(e);
      setCompleteProgressNow(true);
      const next = (consecutiveGenErrorsRef.current || 0) + 1;
      consecutiveGenErrorsRef.current = next;
      if (next >= 2) {
        setGenError(
          <span>
            Se produjo un error. No se consumieron imágenes. Es posible que los servidores estén momentaneamente en mantenimento. Si prefieres puedes contactarte con el{' '}
            <button
              type="button"
              onClick={() => navigate('/ayuda')}
              className="font-semibold underline"
            >
              servicio de ayuda
            </button>
            .
          </span>
        );
      } else {
        setGenError('Se produjo un error. No se consumieron imágenes. Por favor, volvé a intentarlo.');
      }
      setGenerating(false);
      generatingLockRef.current = false;
    }
  };

  return (
    <div className="p-4 sm:p-8 sm:pb-0 max-w-4xl mx-auto min-h-[100dvh] md:min-h-screen bg-white sm:bg-transparent" style={{ pointerEvents: clickShield ? 'none' : 'auto' }}>
      {!showResults ? (
      <div className="sm:bg-white sm:rounded-2xl sm:shadow-sm p-0 sm:p-6">
        <AppleHeader title="Elegir escenario" />

        {/* Progress */}
        {genError && (
          <div className="mb-3 bg-red-50 text-red-700 px-4 py-3 text-sm">{genError}</div>
        )}
        <div className="mt-4">
          <div className="w-full h-1 bg-gray-200 rounded-full">
            <div className="h-1 bg-blue-500 rounded-full" style={{ width: '85%' }} />
          </div>
        </div>

        {/* Más utilizados */}
        <div className="mt-6">
          <h2 className="text-sm font-semibold text-gray-900">Más utilizados</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-3">
            {/* Fondo sugerido por IA */}
            <button
              type="button"
              onClick={pickFondoIA}
              className={`w-full text-left border rounded-2xl p-3 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${selectedBgMode === 'ia' ? 'ring-2 ring-blue-500 ring-offset-2 border-blue-500' : 'border-gray-200'}`}
            >
              <div className="relative overflow-hidden rounded-xl h-28 bg-gradient-to-br from-indigo-400 via-purple-400 to-pink-400 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-white drop-shadow" />
                {selectedBgMode === 'ia' && (
                  <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white">
                    <Check className="w-4 h-4" />
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-900 mt-2 font-semibold">Escenario sugerido por IA</p>
            </button>

            {/* Fondo blanco */}
            <button
              type="button"
              onClick={pickFondoBlanco}
              className={`w-full text-left border rounded-2xl p-3 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${selectedBgMode === 'blanco' ? 'ring-2 ring-blue-500 ring-offset-2 border-blue-500' : 'border-gray-200'}`}
            >
              <div className="relative overflow-hidden rounded-xl h-28 bg-gray-100">
                <CachedImg
                  src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/fondos/Estudio/Fondo_estudio_gris.jpeg"
                  alt="Fondo blanco"
                  className="w-full h-full object-cover"
                />
                {selectedBgMode === 'blanco' && (
                  <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white">
                    <Check className="w-4 h-4" />
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-900 mt-2 font-semibold">Fondo blanco</p>
            </button>

            {/* Describir fondo personalizado */}
            <div
              className={`w-full text-left border rounded-2xl p-3 hover:bg-gray-50 transition-colors ${selectedBgMode === 'descripcion' ? 'ring-2 ring-blue-500 ring-offset-2 border-blue-500 col-span-2' : 'border-gray-200'}`}
            >
              <div className={`flex ${selectedBgMode === 'descripcion' ? 'flex-col sm:flex-row' : 'flex-col'} gap-3`}>
                <button
                  type="button"
                  onClick={() => { setSelectedBgMode('descripcion'); setSelectedBgUrl(''); setSelectedBgId(null); }}
                  className={`relative overflow-hidden rounded-xl h-28 ${selectedBgMode === 'descripcion' ? 'sm:w-48' : 'w-full'} flex-shrink-0 focus:outline-none hover:bg-gray-50`}
                >
                  <div className="relative overflow-hidden rounded-xl h-full w-full bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 flex items-center justify-center">
                    <Type className="w-8 h-8 text-white drop-shadow" />
                    {selectedBgMode === 'descripcion' && (
                      <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white">
                        <Check className="w-4 h-4" />
                      </span>
                    )}
                  </div>
                </button>
                {selectedBgMode === 'descripcion' && (
                  <div className="flex-1">
                    <textarea
                      value={backgroundText}
                      onChange={(e) => setBackgroundText(e.target.value)}
                      placeholder="Describí el fondo que querés..."
                      rows={4}
                      className="mt-1 block w-full rounded-md border-0 resize-none bg-transparent text-sm focus:outline-none"
                    />
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-900 mt-2 font-semibold">Describir escenario personalizado</p>
            </div>
          </div>
        </div>

        {/* Biblioteca de fondos */}
        {/* Horizontal category list + grid of selected category */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Biblioteca de escenarios</h2>
            {loading && <span className="text-sm text-gray-500">Cargando…</span>}
          </div>
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
          {!loading && fondosPorCategoria.length === 0 && (
            <p className="text-sm text-gray-600 mt-2">No hay fondos disponibles por el momento.</p>
          )}
          {!loading && fondosPorCategoria.length > 0 && (
            <>
              {/* Horizontal categories */}
              <div className="mt-3 overflow-x-auto">
                <div className="inline-flex gap-2">
                  {categorias.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setActiveCategoria(cat)}
                      className={`px-4 h-9 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 whitespace-nowrap ${activeCategoria === cat ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Grid for active category */}
              <div className="mt-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {itemsCategoriaActual.map((fondo) => {
                    const isSelected = selectedBgMode === 'elegido' && (selectedBgId === fondo.id || selectedBgUrl === fondo.link_img);
                    return (
                      <button
                        key={fondo.id}
                        type="button"
                        onClick={() => pickFondoImagen(fondo.link_img, fondo.id)}
                        className={`w-full text-left border rounded-2xl p-3 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${isSelected ? 'ring-2 ring-blue-500 ring-offset-2 border-blue-500' : 'border-gray-200'}`}
                      >
                        <div className="relative overflow-hidden rounded-xl h-28 bg-gray-100">
                          <CachedImg src={fondo.link_img} alt={fondo.nombre || `Fondo ${fondo.id}`} className="w-full h-full object-cover" />
                          {isSelected && (
                            <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white">
                              <Check className="w-4 h-4" />
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 mt-2 truncate">{fondo.nombre || 'Fondo'}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer desktop */}
        <div className="mt-6 hidden sm:flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Volver
          </button>
          {!shouldGoToText && composicion === 'producto' ? (
            <button
              type="button"
              className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm ${selectedBgMode && !generating ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-gray-300 text-white cursor-not-allowed'}`}
              onClick={handleGenerate}
              disabled={!selectedBgMode || generating}
            >
              {generating ? (<><Loader2 className="w-4 h-4 animate-spin" /> Generando</>) : (<><Sparkles className="w-4 h-4" /> Generar</>)}
            </button>
          ) : (
            <button
              type="button"
              className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm ${selectedBgMode ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-gray-300 text-white cursor-not-allowed'}`}
              onClick={handleContinue}
              disabled={!selectedBgMode}
            >
              Continuar
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      ) : (
        <div className="mt-2">
          <GeneratedImagesInline
            imageIds={generatedImageIds}
            companyId={companyIdForImages}
            onClose={() => setShowResults(false)}
            onReturnToConfig={() => {
              try { console.log('[ElegirFondoAnuncio] onReturnToConfig called', { modo, composicion, pathname: window.location.pathname, search: window.location.search }); } catch {}
              // Enable click shield and delay hiding results to avoid click-through on underlying buttons
              setClickShield(true);
              setTimeout(() => setShowResults(false), 50);
              setTimeout(() => setClickShield(false), 300);
              try { console.log('[ElegirFondoAnuncio] navigating to seleccionar-productos after closing results'); } catch {}
              const persisted = loadLastCrearAnuncioState();
              const resolvedIds = persisted?.selectedVariantIds && persisted.selectedVariantIds.length > 0
                ? persisted.selectedVariantIds
                : ids;
              const nextModo = persisted?.modo || modo;
              const qp = new URLSearchParams();
              if (resolvedIds.length > 0) qp.set('ids', resolvedIds.join(','));
              if (nextModo) qp.set('modo', nextModo);
              if (persisted?.objetivo) qp.set('objetivo', persisted.objetivo);
              if (persisted?.detallesOferta) qp.set('detalles_oferta', persisted.detallesOferta);
              const query = qp.toString();
              navigate(query ? `/redes-sociales/crear-anuncio/seleccionar-productos?${query}` : '/redes-sociales/crear-anuncio/seleccionar-productos');
            }}
            onGenerateMore={handleGenerateMore}
            onReady={() => setGenerating(false)}
            preloadedImages={preloadedImagesForMore}
          />
        </div>
      )}

      {/* Mobile spacer for fixed bottom bar */}
      {!showResults && (
        <div className="sm:hidden" style={{ height: 'calc(env(safe-area-inset-bottom, 0px) + 64px)' }} />
      )}

      {/* Mobile bottom bar */}
      {!showResults && (
        <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-30">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
            >
              Volver
            </button>
            {!shouldGoToText && composicion === 'producto' ? (
              <button
                type="button"
                className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm ${selectedBgMode && !generating ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-gray-300 text-white cursor-not-allowed'}`}
                onClick={handleGenerate}
                disabled={!selectedBgMode || generating}
              >
                {generating ? (<><Loader2 className="w-4 h-4 animate-spin" /> Generando</>) : (<><Sparkles className="w-4 h-4" /> Generar</>)}
              </button>
            ) : (
              <button
                type="button"
                className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm ${selectedBgMode ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-gray-300 text-white cursor-not-allowed'}`}
                onClick={handleContinue}
                disabled={!selectedBgMode}
              >
                Continuar
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}
      {generating && (
        <LoadingAnimation
          message="Generando imágenes..."
          subMessage="Esto puede tardar unos segundos"
          durationMs={progressDurationMs}
          completeNow={completeProgressNow}
          onCompleted={() => { if (!willShowResultsRef.current) setGenerating(false); }}
        />
      )}
      {/* Subscription limit modal */}
      <SubscriptionLimitModal
        isOpen={showSubscriptionLimitModal}
        onClose={() => setShowSubscriptionLimitModal(false)}
        type={subscriptionLimitType}
      />
    </div>
  );
};

export default ElegirFondoAnuncio;
